// React
import { useMemo } from "react";

// Third-party
import { ShieldAlert, ShieldCheck, Clock } from "lucide-react";

// Hooks
import { useTranslation } from "@/shared/hooks";

/**
 * Verification status type
 */
export type VerificationStatus =
  | "unverified"
  | "pending"
  | "verified"
  | "rejected";

/**
 * Props for VerificationBadge component
 */
interface VerificationBadgeProps {
  status: VerificationStatus;
  className?: string;
}

/**
 * Get badge configuration based on status
 */
function getBadgeConfig(
  status: VerificationStatus,
  t: ReturnType<typeof useTranslation>,
) {
  switch (status) {
    case "verified":
      return {
        icon: ShieldCheck,
        bgColor: "bg-green-100",
        textColor: "text-green-800",
        iconColor: "text-green-600",
        label: t("verificationVerified"),
      };
    case "pending":
      return {
        icon: Clock,
        bgColor: "bg-yellow-100",
        textColor: "text-yellow-800",
        iconColor: "text-yellow-600",
        label: t("verificationPending"),
      };
    case "rejected":
    case "unverified":
    default:
      return {
        icon: ShieldAlert,
        bgColor: "bg-red-100",
        textColor: "text-red-800",
        iconColor: "text-red-600",
        label:
          status === "rejected"
            ? t("verificationRejected")
            : t("verificationUnverified"),
      };
  }
}

/**
 * VerificationBadge Component
 *
 * Displays verification status badge for partners:
 * - Red: Unverified/Rejected
 * - Yellow: Verification in progress
 * - Green: Verified
 */
export function VerificationBadge({
  status,
  className = "",
}: VerificationBadgeProps) {
  const t = useTranslation();

  const config = useMemo(() => getBadgeConfig(status, t), [status, t]);
  const Icon = config.icon;

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${config.bgColor} ${config.textColor} ${className}`}
    >
      <Icon className={`w-3.5 h-3.5 ${config.iconColor}`} />
      {config.label}
    </span>
  );
}
